import React, { useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Alert, Grid, TextField, Typography } from '@mui/material';

import { useAppSelector } from '../../../../app/hooks';
import { Survey } from '../../../../interfaces/survey.interface';
import { AssessmentSurvey } from '../../../../interfaces/assessment.interface';
import {
  Application,
  ApplicationVersion,
  Company,
  CompanyApplication,
  CompanyDocument,
  CompanyDocumentSubmitData,
  CompanyDocumentUpdateData,
  Contact,
  Version,
} from '../../../../interfaces/users.interface';
import createCompanyDocument, { updateCompanyDocument } from '../../../../services/companyDocuments';
import { createApplicationVersion } from '../../../../services/applications';

import FileUploaderComp from '../../../Forms/FileUpload';
import MultipleSelectChip from '../../../Vendors/Forms/MultiSelect';
import { StandardDialogActions } from '../../../Modals/StandardDialog';
import { AsyncSaveButton } from '../../../../components/buttons/AsyncSaveButton';

const applicationCompanyId = (application: Application) =>
  typeof application.company === 'number' ? application.company : application.company?.id;

// Shared by the v1 accordion and the v2 tab so both surfaces upload identically.
export default function CompanyDocumentUploadForm(props: {
  company: Company;
  uploadCallback: (updatedCompany: Company) => void;
}) {
  const { company, uploadCallback } = props;
  const { handleSubmit, register } = useForm();
  const { results: assessments } = useAppSelector((state) => state.assessments);
  const [documentError, setDocumentError] = useState('');
  const [selectedCompanyDocument, setSelectedCompanyDocument] = useState<File | undefined>(undefined);
  const [selectedSites, setSelectedSites] = useState<number[]>([]);
  const [selectedServices, setSelectedServices] = useState<number[]>([]);
  const [selectedApplications, setSelectedApplications] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const locationOptions = (locations: Contact[] = []) => {
    if (!assessments) return locations;
    return locations.filter(
      (loc) =>
        !assessments.find((ass) =>
          (ass.surveys as AssessmentSurvey[])?.find((asssur) => (asssur.survey as Survey).site === loc.id),
        ),
    );
  };

  const applicationOptions = (companyApplications?: CompanyApplication[]) => {
    if (!companyApplications?.length) return [];
    companyApplications = companyApplications.filter(
      (companyApp) =>
        applicationCompanyId(companyApp.application) === company.id &&
        companyApp.application.versions &&
        companyApp.application.versions.length > 0,
    );
    let options: Array<Application | Version> = [];
    companyApplications.forEach((ca) => {
      options = options.concat([ca.application, ...(ca.application.versions as Version[])]);
    });
    return options;
  };

  const handleCompanyDocumentUpdate = async (companyDoc: CompanyDocument) => {
    const applicationVersionPairs: ApplicationVersion[] = [];
    company.companyApplications?.forEach((ca) => {
      if (applicationCompanyId(ca.application) !== company.id) return;
      (ca.application.versions as Version[])?.forEach((cav) => {
        if (selectedApplications.includes(cav.id!)) {
          applicationVersionPairs.push({ application: ca.application.id!, version: cav.id! });
        }
      });
    });
    const applicationVersions = await Promise.all(
      applicationVersionPairs.map((pair) => createApplicationVersion(pair)),
    ).then((created) => created.map((av) => av.id!));

    const updateRequest: CompanyDocumentUpdateData = {
      name: companyDoc.name,
      company: companyDoc.company,
      sites: selectedSites,
      services: selectedServices,
      applicationVersions,
    };
    return updateCompanyDocument(companyDoc.id, updateRequest);
  };

  const onSubmit = async (data: FieldValues) => {
    if (!company?.id) return;
    if (!selectedCompanyDocument) {
      setDocumentError('Please Select A File to Upload');
      return;
    }

    setIsSaving(true);
    const newCompanyDoc: CompanyDocumentSubmitData = {
      name: data.name,
      document: selectedCompanyDocument,
      company: company.id,
    };

    try {
      let companyDoc = await createCompanyDocument(newCompanyDoc);
      companyDoc = await handleCompanyDocumentUpdate(companyDoc);
      const companyDocs: CompanyDocument[] = company.companyDocuments ? company.companyDocuments : [];
      setDocumentError('');
      uploadCallback({ ...company, companyDocuments: [...companyDocs, companyDoc] });
    } catch (error: any) {
      setDocumentError(error?.response?.data?.document?.[0] || 'We could not upload your document. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Grid item xs={12} mt={2} component='form' method='post' onSubmit={handleSubmit(onSubmit)}>
      <Grid item xs={12}>
        <TextField
          required
          fullWidth
          margin='normal'
          id='name'
          label='Description'
          autoFocus
          {...register('name')}
        />
      </Grid>
      <Grid item xs={12} mt={2}>
        <FileUploaderComp
          showCOPublicModal={true}
          enablePublic
          assessorVisible={false}
          multiple={false}
          callback={(file: any) => {
            setDocumentError('');
            setSelectedCompanyDocument(file[0].file as File);
          }}
        />
        {documentError && (
          <Alert severity='error' sx={{ mt: 1 }}>
            {documentError}
          </Alert>
        )}
      </Grid>
      <Typography variant='caption'>
          Please be advised that documents uploaded to this section, including your Legacy TPN Assessment Reports, will not be watermarked upon user download--this includes Content Owners. If you require watermarking, please direct Content Owners to the TPN Box account.
      </Typography>
      <Grid item xs={12} mt={2}>
        <MultipleSelectChip
          options={locationOptions(company.locations)}
          optionsCallback={setSelectedSites}
          selectedOptions={selectedSites}
          inputLabel='Sites'
        />
      </Grid>
      <Grid item xs={12} mt={2}>
        <MultipleSelectChip
          options={company.services || []}
          optionsCallback={setSelectedServices}
          selectedOptions={selectedServices}
          inputLabel='Services'
        />
      </Grid>
      <Grid item xs={12} my={2}>
        <MultipleSelectChip
          options={applicationOptions(company.companyApplications)}
          optionsCallback={setSelectedApplications}
          selectedOptions={selectedApplications}
          inputLabel='Applications'
          isApplication={true}
        />
      </Grid>
      <StandardDialogActions>
        <AsyncSaveButton
          type='submit'
          variant='contained'
          label={'Upload'}
          savingLabel="Uploading..."
          isSaving={isSaving}
        />
      </StandardDialogActions>
    </Grid>
  );
}
